import { useContext, useEffect, useState } from 'react';
import { graphqlFetch } from '@shared/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { previewQueryDocument } from '@shared/api/documents/preview';

import { AppContext } from '../../App.context';
import { isNumeric, Issue, USER_ROLE } from '../../constants';
import config from '../../config';
import { useIssueNotFound } from '@shared/lib/hooks';
import { getQueryCacheOptions } from '@shared/query-client/QueryClient';

/**
 * React hook that returns the details of the issue
 *
 * @param id - Issue identifier
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const queryClient = useQueryClient();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { onlineStatus: isOnline } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getIssue,
	});
	const queryKey = ['preview', id];

	// Before even trying to load: if we're offline and there's no cached
	// data for this exact query key, we already have the answer -- no need
	// to attempt the fetch (and briefly show a loading state) just to have
	// it immediately fail with nothing to show.
	const isUnavailableOffline =
		!isOnline && queryClient.getQueryData(queryKey) === undefined;

	// The generated PreviewIssueQuery type is honest that server fields can
	// be null (e.g. the schema's `issue.index`/`isFree`/`pages` are all
	// nullable) where the hand-rolled `Issue` interface claims them
	// required; reconciling that is topic 5's "derive Issue/Article/Style
	// from generated types" step, not something to patch per call site.
	// This cast keeps behaviour identical to before this query was typed at
	// all (the old `useQuery<PreviewQuery>` already claimed `Issue`
	// unchecked) -- it makes the gap visible, not new.
	const { isPending, error, data } = useQuery({
		queryKey,
		enabled: !isUnavailableOffline,
		queryFn: async () =>
			graphqlFetch(previewQueryDocument, {
				id,
				isSlug: !isNumeric(id),
			}) as unknown as { issue: Issue | null },
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;
		setIssue(data.issue);
	}, [data]);

	return {
		isLoading: isUnavailableOffline ? false : isPending,
		isUnavailableOffline,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

export interface GetIssueResponse {
	issue: Issue | null | undefined;
	isLoading: boolean;
	isUnavailableOffline: boolean;
	error?: Error | null;
}
